import { TextField } from "@kora/kit/primitives/TextField/TextField";
import { useEffect, useState } from "react";
import { Failure, GhostButton, Loading, Messages, Note, PrimaryButton, Screen } from "../components/Entry";
import { copy } from "../lib/copy";
import { errorsFor, go, nodeNamed, useFlow, valueOf } from "../lib/flow";
import { whoami, type Flow } from "../lib/kratos";
import { href, navigate } from "../lib/router";

const isVerified = async (email: string) => {
  const session = await whoami();
  return Boolean(session?.identity.verifiable_addresses?.some(a => a.verified && a.value.toLowerCase() === email.toLowerCase()));
};

export function Verification({ query }: { query: URLSearchParams }) {
  const state = useFlow("verification", "/verification", query, { return_to: query.get("return_to") ?? undefined });
  const { flow } = state;
  if (!flow) return state.failure ? <Screen title="Verify email"><Failure text={state.failure} /></Screen> : <Loading />;
  if (flow.state === "passed_challenge") return <Verified flow={flow} />;
  // ?sent=1: the email went out with the sign-up, so the page starts at "check your email".
  if (flow.state === "sent_email" || query.get("sent") === "1") return <Sent flow={flow} state={state} initial={query.get("email") ?? ""} />;
  return <Ask flow={flow} state={state} initial={query.get("email") ?? ""} />;
}

type State = ReturnType<typeof useFlow>;

function Ask({ flow, state, initial }: { flow: Flow; state: State; initial: string }) {
  const [email, setEmail] = useState(() => valueOf(flow, "email") || initial);
  return (
    <Screen title="Verify email">
      <form
        noValidate
        className="entry-form"
        onSubmit={event => {
          event.preventDefault();
          void state.submit({ method: nodeNamed(flow, "code") ? "code" : "link", email: email.trim() }, () => undefined);
        }}
      >
        <Note>We’ll email you a link to confirm your address.</Note>
        <TextField
          label="Email"
          inputType="email"
          inputProps={{ autoComplete: "email", name: "email" }}
          value={email}
          onChange={setEmail}
          error={errorsFor(flow, "email").map(copy)[0]}
          size="lg"
        />
        <Messages messages={flow.ui.messages ?? []} />
        <Failure text={state.failure} />
        <PrimaryButton pending={state.pending}>Send link</PrimaryButton>
        <GhostButton onClick={() => navigate("/login")}>Back to sign in</GhostButton>
      </form>
    </Screen>
  );
}

function Sent({ flow, state, initial = "" }: { flow: Flow; state: State; initial?: string }) {
  const email = valueOf(flow, "email") || initial;
  const byCode = Boolean(nodeNamed(flow, "code"));
  const [code, setCode] = useState("");
  const [verified, setVerified] = useState(false);

  // The link usually opens in another tab; this one follows along once the address is verified.
  useEffect(() => {
    if (byCode || !email) return;
    const timer = window.setInterval(async () => {
      if (await isVerified(email)) setVerified(true);
    }, 3000);
    return () => window.clearInterval(timer);
  }, [byCode, email]);
  if (verified) return <Verified flow={flow} />;

  return (
    <Screen title="Check your email">
      <form
        noValidate
        className="entry-form"
        onSubmit={event => {
          event.preventDefault();
          if (byCode) void state.submit({ method: "code", code }, () => undefined);
          else void state.submit({ method: "link", email }, () => undefined);
        }}
      >
        {byCode ? (
          <>
            <Note>Enter the code for {email}.</Note>
            <TextField
              label="Verification code"
              value={code}
              onChange={value => setCode(value.replace(/\D/g, "").slice(0, 6))}
              inputProps={{ inputMode: "numeric", autoComplete: "one-time-code", maxLength: 6, name: "code" }}
              error={errorsFor(flow, "code").map(copy)[0]}
              size="lg"
            />
          </>
        ) : (
          <Note>We sent a verification link to {email || "your email"}. Open it to finish.</Note>
        )}
        <Messages messages={(flow.ui.messages ?? []).filter(m => m.id !== 1080001)} />
        <Failure text={state.failure} />
        {byCode ? (
          <>
            <PrimaryButton pending={state.pending}>Verify and continue</PrimaryButton>
            <GhostButton onClick={() => void state.submit({ method: "code", email }, () => undefined)} disabled={state.pending}>
              Send another code
            </GhostButton>
          </>
        ) : (
          <GhostButton type="submit" disabled={state.pending}>
            Send another link
          </GhostButton>
        )}
        <GhostButton onClick={() => navigate(href("/verification", { email }))}>Change email</GhostButton>
      </form>
    </Screen>
  );
}

function Verified({ flow }: { flow: Flow }) {
  return (
    <Screen title="Email verified">
      <form
        noValidate
        className="entry-form"
        onSubmit={event => {
          event.preventDefault();
          go(flow.return_to || "/");
        }}
      >
        <Note>Your email is verified.</Note>
        <PrimaryButton>Continue</PrimaryButton>
      </form>
    </Screen>
  );
}
